import { useEffect, useMemo, useState } from 'react';
import { useUser } from 'src/context/UserProvider';
import { Assembly } from 'src/services/assemblies';
import {
  getConstraintsOfPieces,
  getModificationsOfPieces,
  Modification,
} from 'src/services/configurator';
import { getGranits } from 'src/services/granits';
import {
  getModificationWithHistory,
} from 'src/utils/configurator.utils';
import { Vector3, WebGLRenderer } from 'three';

import { getAdminEngravingsPrice } from 'src/services/coefficients';
import { getSettings } from '../services/pieces';
import { useImportPiece } from './useImportPiece';
import useLoadDataList from './useLoadDataList';
import useLoadData from './useLoadData';
import { HistoryMap } from './useConfigurator.utils';

import { useAssemblyInit } from './useAssemblyInit';
import { useCameraControl } from './useCameraControl';
import { useCollision } from './useCollision';
import { usePointSelection } from './usePointSelection';
import { useGeometryManipulation } from './useGeometryManipulation';
import { useAssemblyDetails } from './useAssemblyDetails';

// Re-export pour compatibilité avec les imports existants
export type { PieceDetail } from './useConfigurator.utils';

export const useConfigurator = (
  assembly: Assembly | null,
  orbitRef: React.MutableRefObject<any>,
  gl: WebGLRenderer | undefined,
) => {
  const { user } = useUser();
  const { data: adminEngravingsPrice } = useLoadData(() =>
    getAdminEngravingsPrice(),
  );
  const { data: settings } = useLoadData(() => getSettings());

  // --- Initialisation de l'assemblage ---
  const {
    customSteles,
    setCustomSteles,
    customPlatings,
    setCustomPlatings,
    customSoles,
    setCustomSoles,
    customBoxes,
    setCustomBoxes,
    activePieces,
    setActivePieces,
    patterns,
    setPatterns,
  } = useAssemblyInit(assembly);

  // --- État partagé ---
  const [pieceIndex, setPieceIndex] = useState<number>(0);
  const [history, setHistory] = useState<HistoryMap>({});
  const [originalGeometry, setOriginalGeometry] = useState<any>();
  const [reloadPiece, setReloadPiece] = useState<boolean>(false);

  // --- Import de pièces ---
  const { pieces, removeImportedPiece } = useImportPiece(
    activePieces,
    setActivePieces,
    history,
    setHistory,
  );

  // --- Données chargées ---
  const { data: granits, onRefresh: refreshGranits } =
    useLoadDataList(getGranits);

  const [activePiecesIds, setActivePiecesIds] = useState<number[]>([]);

  useEffect(() => {
    if (activePieces && activePieces.length > 0) {
      const ids = [...new Set(activePieces.map((element) => element.id))].sort(
        (a, b) => (a > b ? 1 : -1),
      );

      if (ids?.toString() !== activePiecesIds?.toString()) {
        setActivePiecesIds(ids);
      }
    }
  }, [activePieces]);

  useEffect(() => {
    const copyOfOldOriginalGeometry = originalGeometry
      ? [...originalGeometry]
      : [];

    const originalGeometryCopy = pieces.map((element, index) => {
      if (copyOfOldOriginalGeometry[index]) {
        return copyOfOldOriginalGeometry[index];
      }
      return new Float32Array(element.geometry.attributes.position.array);
    });

    setOriginalGeometry(originalGeometryCopy);
  }, [pieces]);

  // --- Contraintes & modifications ---
  const { data: constraintsOfPieces, onRefresh: refreshConstraints } =
    useLoadDataList(
      () =>
        getConstraintsOfPieces([
          ...new Set(activePieces.map((element) => element.id)),
        ]),
      [activePiecesIds],
    );

  const constraints = useMemo(() => {
    const constraintsOfPiece = constraintsOfPieces?.find(
      (element) => element.id === pieces[pieceIndex]?.id,
    );

    return constraintsOfPiece?.constraints;
  }, [pieceIndex, constraintsOfPieces, pieces]);

  const { data: modificationsOfPieces, onRefresh: refreshModifications } =
    useLoadDataList(
      () =>
        getModificationsOfPieces([
          ...new Set(activePieces.map((element) => element.id)),
        ]),
      [activePiecesIds],
    );

  const modifications = useMemo(() => {
    const modificationsOfPiece = modificationsOfPieces?.find(
      (element) => element.id === pieces[pieceIndex]?.id,
    );
    return modificationsOfPiece?.modifications;
  }, [pieceIndex, modificationsOfPieces, pieces]);

  const [userModifications, setUserModifications] = useState<
    Modification[] | undefined
  >(modifications);

  useEffect(() => {
    if (modifications) {
      const updatedModifications = modifications.map((el) => {
        const elPoints = el.points.sort((a, b) => {
          if (a.index !== undefined && b.index !== undefined) {
            return a.index < b.index ? -1 : 1;
          }
          return 0;
        });

        if (!elPoints[0] || !elPoints[1]) return null;
        const newP1 = getModificationWithHistory(
          history[pieces[pieceIndex].uuid],
          elPoints[0],
        );
        const newP2 = getModificationWithHistory(
          history[pieces[pieceIndex].uuid],
          elPoints[1],
        );

        const vectorP1 = new Vector3(newP1.x, newP1.y, newP1.z);
        const vectorP2 = new Vector3(newP2.x, newP2.y, newP2.z);

        let distance = Number.parseFloat(
          vectorP1.distanceTo(vectorP2).toFixed(2),
        );

        if (el.simpleModeAxis) {
          distance =
            vectorP2[el.simpleModeAxis] > vectorP1[el.simpleModeAxis]
              ? +(
                  vectorP2[el.simpleModeAxis] - vectorP1[el.simpleModeAxis]
                ).toFixed(2)
              : +(
                  vectorP1[el.simpleModeAxis] - vectorP2[el.simpleModeAxis]
                ).toFixed(2);
        }

        return {
          ...el,
          distance,
        };
      });

      setUserModifications(
        updatedModifications.filter((el) => el !== null) as any,
      );
    }
  }, [modifications, pieceIndex]);

  // --- Suppression de pièce ---
  const removeOnePiece = (pieceIndexToRemove: number) => {
    if (pieceIndexToRemove > activePieces.length - 1) {
      const customStele =
        customSteles[pieceIndexToRemove - activePieces.length];
      const newPatterns = { ...patterns };
      delete newPatterns[customStele.id];
      setPatterns(newPatterns);
      setCustomSteles(
        customSteles.filter((el, index) => el.id !== customStele.id),
      );
    } else if (activePieces[pieceIndexToRemove]?.underAssembly) {
      const indexToDelete: number[] = [];
      for (const [elIndex, el] of activePieces.entries()) {
        if (
          el.underAssembly &&
          el.underAssembly.uuid ===
            activePieces[pieceIndexToRemove]?.underAssembly.uuid
        ) {
          indexToDelete.push(elIndex);
        }
      }
      const newPatterns = { ...patterns };
      for (const el of indexToDelete) {
        delete newPatterns[activePieces[el].uuid];
      }

      const newActivePiece = activePieces.filter(
        (el, index) => !indexToDelete.includes(index),
      );

      setActivePieces(newActivePiece);
      setPatterns(newPatterns);
      removeImportedPiece(indexToDelete);
      setPieceIndex(newActivePiece.length - 1);
    } else {
      const piece = activePieces[pieceIndexToRemove];
      const newPatterns = { ...patterns };
      delete newPatterns[piece.uuid];

      const newActivePiece = activePieces.filter(
        (el, index) => index !== pieceIndexToRemove,
      );

      setActivePieces(newActivePiece);
      removeImportedPiece(pieceIndexToRemove);
      setPieceIndex(newActivePiece.length - 1);
      setPatterns(newPatterns);
    }
  };

  // --- Sub-hooks ---
  const { centerCamera } = useCameraControl(
    pieces,
    customPlatings,
    customSoles,
    customBoxes,
    customSteles,
    orbitRef,
    gl,
  );

  const { checkCollision } = useCollision(
    pieces,
    activePieces,
    centerCamera,
    setReloadPiece,
  );

  const {
    selectedPoints,
    setSelectedPoints,
    movePieceOnAxe,
    findPointOnTheSameAxe,
    findPointAround,
  } = usePointSelection(pieces, pieceIndex, setReloadPiece);

  const { changePoint, changeGeometry, changePosition } =
    useGeometryManipulation(
      pieces,
      pieceIndex,
      history,
      setHistory,
      constraints,
      userModifications,
      setUserModifications,
      setReloadPiece,
      checkCollision,
    );

  const { getAssemblyDetails, getAssemblySize } = useAssemblyDetails(
    pieces,
    activePieces,
    customSteles,
    customPlatings,
    customSoles,
    customBoxes,
    patterns,
    assembly,
    settings,
    adminEngravingsPrice,
  );

  return {
    geometry: pieces[pieceIndex]
      ? pieces[pieceIndex].geometry.attributes.position.array
      : [],
    selectedPoints,
    constraints,
    userModifications,
    history,
    pieces,
    pieceIndex,
    reloadPiece,
    customSteles,
    activePieces,
    customPlatings,
    granits,
    patterns,
    setSelectedPoints,
    changePoint,
    refreshConstraints,
    refreshModifications,
    setPieceIndex,
    setReloadPiece,
    changeGeometry,
    setActivePieces,
    changePosition,
    setCustomSteles,
    removeOnePiece,
    movePieceOnAxe,
    checkCollision,
    setCustomPlatings,
    findPointOnTheSameAxe,
    findPointAround,
    refreshGranits,
    getAssemblyDetails,
    setPatterns,
    getAssemblySize,
    centerCamera,
    setCustomSoles,
    customSoles,
    setCustomBoxes,
    customBoxes,
  };
};
